// ─── El kit del CATÁLOGO: una receta fija ───────────────────────────────────
// "Set escolar" = 2 cuadernos + 3 lápices. A diferencia del kit de LÍNEA —la
// suculenta, cuya composición cambia en cada pedido y por eso vive en los
// costos de la línea (ver kit.js)— este kit es un producto del catálogo con su
// receta guardada en la ficha. Los dos existen porque son dos realidades
// distintas: una composición que se negocia pedido a pedido y una que es
// siempre la misma.
//
// Lo que define a un kit de catálogo:
//
//   · NO ES INVENTARIABLE. El set escolar no entra nunca al kardex: no se
//     compra ni se almacena, se arma al entregar. Inventariables son sus
//     componentes. De ahí que su stock no se guarde: se CALCULA.
//   · SE VENDE Y SE FACTURA como un solo artículo. Al cliente le llega "Set
//     escolar", no dos cuadernos y tres lápices.
//   · SU COSTO SE DERIVA de sus partes. Un costo digitado en la ficha del kit
//     sería un número que envejece: en cuanto sube el cuaderno, el margen del
//     set es mentira.
//
// Este módulo es una HOJA a propósito: no importa nada más que el estado, para
// que accounting.js pueda preguntarle por el stock de un kit sin cerrar un
// ciclo de imports (kit.js sí depende de accounting).
import { state } from "./state.js";
import { parseNumber } from "./utils.js";

function norm(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();
}

export function productoPorNombre(nombre) {
  const n = (nombre || "").trim();
  if (!n) return null;
  const prods = state.data.productos || [];
  return prods.find(p => (p.nombre || "").trim() === n)
      || prods.find(p => norm(p.nombre) === norm(n))
      || null;
}

// Un producto es kit cuando lo dice su ficha Y tiene con qué armarse. La
// segunda mitad importa: un kit marcado pero vacío se comportaría como un
// artículo sin stock que nadie puede entregar ni explicar.
export function esProductoKit(prod) {
  const p = typeof prod === "string" ? productoPorNombre(prod) : prod;
  if (!p || !p.es_kit) return false;
  return componentesDeProducto(p).length > 0;
}

// Los componentes de la receta, ya resueltos contra el catálogo. Un componente
// que no existe (se borró el producto, se renombró) se descarta: sin ficha no
// hay kardex del que sacarlo ni costo que sumar.
export function componentesDeProducto(prod) {
  const p = typeof prod === "string" ? productoPorNombre(prod) : prod;
  if (!p || !p.es_kit) return [];
  return (p._componentesKit || [])
    .map(c => {
      const comp = productoPorNombre(c.articulo);
      const cant = parseNumber(c.cantidad) || 0;
      if (!comp || cant <= 0) return null;
      return { articulo: comp.nombre, cantidad: cant, producto: comp };
    })
    .filter(Boolean);
}

// Lo que cuesta armar UNA unidad, con los costos del catálogo de sus partes.
// Devuelve null si no es kit, para que quien pregunte distinga "no aplica" de
// "cuesta cero".
export function costoKitCatalogo(prod) {
  const comps = componentesDeProducto(prod);
  if (!comps.length) return null;
  return comps.reduce((s, c) => s + c.cantidad * (parseNumber(c.producto.costo) || 0), 0);
}

// El costo base de una línea: el del kit se deriva de sus partes, el de
// cualquier otro producto es el digitado en su ficha. Es la puerta que usan la
// cotización y la orden para no tener que saber si el artículo es un kit.
export function costoBaseProducto(prod) {
  const derivado = costoKitCatalogo(prod);
  return derivado != null ? derivado : (parseNumber(prod && prod.costo) || 0);
}

// "2 Cuaderno cosido 100 hojas + 3 Lápiz HB". Es lo que se imprime bajo el
// artículo en la cotización, la remisión y la factura: el cliente compra un set
// y tiene derecho a saber qué trae.
export function descripcionKitCatalogo(prod) {
  return componentesDeProducto(prod)
    .map(c => `${c.cantidad} ${c.articulo}`).join(" + ");
}

// ─── Lo que hay que COMPRAR para armar un kit ───────────────────────────────
// Un kit no se compra: se compran sus partes. Y la receta puede llevar otro kit
// dentro, así que se baja hasta los artículos que SÍ se compran (los que tienen
// ficha de inventario), multiplicando las cantidades por el camino: 20 sets de
// 2 cuadernos son 40 cuadernos.
//
// Lleva el camino recorrido en un conjunto porque un ciclo A→B→A colgaría el
// navegador, igual que en el cálculo del stock (ver accounting.js).
export function partesCompradasDeKit(prod, cantidad = 1, camino) {
  const p = typeof prod === "string" ? productoPorNombre(prod) : prod;
  if (!p) return [];
  const comps = componentesDeProducto(p);
  if (!comps.length) return [];
  const visto = camino || new Set();
  const clave = norm(p.nombre);
  if (visto.has(clave)) return [];
  visto.add(clave);
  const out = [];
  comps.forEach(c => {
    const necesita = cantidad * c.cantidad;
    // Lo que decide si una parte se compra es que NO sea un kit. Un kit dentro
    // de la receta se baja a las suyas; si el camino se cortó por un ciclo no
    // aporta nada, y eso es correcto: un kit no se compra nunca.
    if (esProductoKit(c.producto)) {
      out.push(...partesCompradasDeKit(c.producto, necesita, visto));
      return;
    }
    out.push({ articulo: c.articulo, cantidad: necesita, producto: c.producto });
  });
  visto.delete(clave);
  return out;
}

// ─── Cuántas unidades se pueden armar ───────────────────────────────────────
// Manda el componente que menos alcance: con 10 cuadernos y 30 lápices se
// arman 5 sets de 2 cuadernos y 3 lápices, no 10.
//
// `stockDe` lo inyecta quien llama (stock bruto o disponible, según lo que esté
// preguntando), y así este módulo no necesita conocer el kardex.
export function armablesDeKit(prod, stockDe) {
  const comps = componentesDeProducto(prod);
  if (!comps.length) return null;
  return comps.reduce((min, c) => {
    const disp = parseNumber(stockDe(c.articulo)) || 0;
    return Math.min(min, Math.floor(disp / c.cantidad));
  }, Infinity);
}

// El detalle de por qué alcanza para tantos, para poder decírselo al usuario en
// vez de dejarlo adivinando cuál parte es la que falta.
export function detalleArmablesKit(prod, stockDe) {
  return componentesDeProducto(prod).map(c => {
    const disp = parseNumber(stockDe(c.articulo)) || 0;
    return { articulo: c.articulo, porUnidad: c.cantidad, disponible: disp,
             alcanzaPara: Math.floor(disp / c.cantidad) };
  });
}
